import { CommonModule } from "@angular/common";
import { Component, Input } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatExpansionModule } from "@angular/material/expansion";
import { CountryISO, NgxIntlTelInputModule, SearchCountryField } from 'ngx-intl-tel-input';
import { PhoneNumberUtil } from 'google-libphonenumber';

@Component({
    selector: 'app-contact-details',
    standalone: true,
    imports: [CommonModule, MatExpansionModule, ReactiveFormsModule, NgxIntlTelInputModule],
    templateUrl: './contact-details.component.html',
    styleUrl: './contact-details.component.scss'
})
export class ContactDetails{
    @Input() companyDetailForm!: FormGroup;
    @Input() formatPhoneNumber : (phoneNumber: string | null, countryCode?: CountryISO | null) => string | null = (phoneNumber: string | null) => null;
    @Input() extractRawPhoneNumber : (phoneNumber: string | null, countryCode?: CountryISO | null) => string | null = (phoneNumber: string | null) => null;
    CountryISO = CountryISO;
    SearchCountryField = SearchCountryField;
    phoneUtil = PhoneNumberUtil.getInstance();
    
    // Get all countries from PhoneNumberUtil for dropdown
    getAllCountries(): { code: CountryISO; name: string; displayName: string }[] {
      const countries: { code: CountryISO; name: string; displayName: string }[] = [];
      try {
        // Get all supported region codes
        const supportedRegions = this.phoneUtil.getSupportedRegions();
        for (const region of supportedRegions) {
          try {
            const countryCode = region as CountryISO;
            const countryCallingCode = this.phoneUtil.getCountryCodeForRegion(region);
            const countryName = this.getCountryName(countryCode);
            const displayName = countryCallingCode ? `+${countryCallingCode} ${countryName}` : countryName;
            countries.push({ code: countryCode, name: countryName, displayName });
          } catch {
            // Skip invalid regions
          }
        }
      } catch {
        // Fallback to common countries if getting all regions fails
        countries.push(
          { code: CountryISO.UnitedStates, name: 'United States', displayName: '+1 United States' },
          { code: CountryISO.UnitedKingdom, name: 'United Kingdom', displayName: '+44 United Kingdom' }
        );
      }
      return countries.sort((a, b) => a.name.localeCompare(b.name));
    }
    
    getCountryName(countryCode: CountryISO): string {
      const codeStr = String(countryCode).toUpperCase();
      
      // Primary: Use browser's Intl.DisplayNames API (standard, supports all countries)
      try {
        const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
        const countryName = displayNames.of(codeStr);
        if (countryName) {
          return countryName;
        }
      } catch (error) {
        // Intl.DisplayNames not supported, fall through to fallback
      }
      
      // Fallback: Only use hardcoded mapping if Intl API is not available
      const countryNames: { [key: string]: string } = {
        'US': 'United States', 'GB': 'United Kingdom', 'CA': 'Canada', 'AU': 'Australia',
        'FR': 'France', 'DE': 'Germany', 'IT': 'Italy', 'ES': 'Spain', 'NL': 'Netherlands',
        'BE': 'Belgium', 'CH': 'Switzerland', 'AT': 'Austria', 'SE': 'Sweden', 'NO': 'Norway',
        'DK': 'Denmark', 'FI': 'Finland', 'PL': 'Poland', 'CZ': 'Czech Republic', 'IE': 'Ireland',
        'PT': 'Portugal', 'GR': 'Greece', 'LU': 'Luxembourg', 'IS': 'Iceland', 'MT': 'Malta',
        'CY': 'Cyprus', 'EE': 'Estonia', 'LV': 'Latvia', 'LT': 'Lithuania', 'SI': 'Slovenia',
        'SK': 'Slovakia', 'HU': 'Hungary', 'RO': 'Romania', 'BG': 'Bulgaria', 'HR': 'Croatia',
        'JP': 'Japan', 'CN': 'China', 'IN': 'India', 'KR': 'South Korea', 'SG': 'Singapore',
        'MY': 'Malaysia', 'TH': 'Thailand', 'PH': 'Philippines', 'ID': 'Indonesia', 'VN': 'Vietnam',
        'NZ': 'New Zealand', 'ZA': 'South Africa', 'BR': 'Brazil', 'MX': 'Mexico', 'AR': 'Argentina',
        'CL': 'Chile', 'CO': 'Colombia', 'PE': 'Peru', 'AE': 'United Arab Emirates', 'SA': 'Saudi Arabia',
        'IL': 'Israel', 'TR': 'Turkey', 'RU': 'Russia', 'UA': 'Ukraine', 'EG': 'Egypt',
        'NG': 'Nigeria', 'KE': 'Kenya', 'GH': 'Ghana', 'MA': 'Morocco', 'TN': 'Tunisia'
      };
      
      if (countryNames[codeStr]) {
        return countryNames[codeStr];
      }
      
      // Final fallback: format the code nicely
      return codeStr.split('').map((char, i) => i === 0 ? char.toUpperCase() : char.toLowerCase()).join('');
    }

    getMaxPhoneLength(countryCode: CountryISO | null | undefined): number {
      if (!countryCode) return 15;
      const regionCode = String(countryCode).toLowerCase();
      
      // Comprehensive country-to-max-length mapping
      const countryMaxLengths: { [key: string]: number } = {
        // Very short numbers (4-5 digits)
        'nu': 4,  // Niue
        'tk': 4,  // Tokelau
        'nf': 5,  // Norfolk Island
        
        // Short numbers (6-7 digits)
        'gi': 8,  // Gibraltar
        'va': 8,  // Vatican City
        'sm': 9,  // San Marino
        'mc': 9,  // Monaco
        'li': 9,  // Liechtenstein
        
        // Standard 10-digit numbers (US, Canada, etc.)
        'us': 10, // United States
        'ca': 10, // Canada
        'ru': 10, // Russia
        'kz': 10, // Kazakhstan
        'mx': 10, // Mexico
        'br': 11, // Brazil (can be 10 or 11)
        'in': 10, // India
        'au': 9,  // Australia
        'nz': 9,  // New Zealand
        'za': 9,  // South Africa
        'ar': 10, // Argentina
        'cl': 9,  // Chile
        'co': 10, // Colombia
        'pe': 9,  // Peru
        've': 10, // Venezuela
        'ec': 9,  // Ecuador
        'uy': 8,  // Uruguay
        'py': 9,  // Paraguay
        'bo': 8,  // Bolivia
        'cr': 8,  // Costa Rica
        'pa': 8,  // Panama
        'gt': 8,  // Guatemala
        'hn': 8,  // Honduras
        'ni': 8,  // Nicaragua
        'sv': 8,  // El Salvador
        'do': 10, // Dominican Republic
        'cu': 8,  // Cuba
        'jm': 10, // Jamaica
        'tt': 10, // Trinidad and Tobago
        'bb': 10, // Barbados
        'bs': 10, // Bahamas
        'bz': 7,  // Belize
        'gy': 7,  // Guyana
        'sr': 7,  // Suriname
        'gf': 9,  // French Guiana
        
        // 11-digit numbers (UK, etc.)
        'gb': 11, // United Kingdom
        'ie': 9,  // Ireland
        'nl': 9,  // Netherlands
        'be': 9,  // Belgium
        'lu': 9,  // Luxembourg
        'ch': 9,  // Switzerland
        'at': 10, // Austria
        'de': 11, // Germany
        'dk': 8,  // Denmark
        'se': 9,  // Sweden
        'no': 8,  // Norway
        'fi': 9,  // Finland
        'is': 7,  // Iceland
        'pl': 9,  // Poland
        'cz': 9,  // Czech Republic
        'sk': 9,  // Slovakia
        'hu': 9,  // Hungary
        'ro': 10, // Romania
        'bg': 9,  // Bulgaria
        'gr': 10, // Greece
        'pt': 9,  // Portugal
        'es': 9,  // Spain
        'it': 10, // Italy
        'mt': 8,  // Malta
        'cy': 8,  // Cyprus
        
        // Eastern Europe
        'ua': 9,  // Ukraine
        'by': 9,  // Belarus
        'lt': 8,  // Lithuania
        'lv': 8,  // Latvia
        'ee': 8,  // Estonia
        'md': 8,  // Moldova
        'rs': 9,  // Serbia
        'hr': 9,  // Croatia
        'si': 8,  // Slovenia
        'ba': 8,  // Bosnia and Herzegovina
        'mk': 9,  // North Macedonia
        'al': 9,  // Albania
        'me': 8,  // Montenegro
        'xk': 8,  // Kosovo
        
        // Middle East
        'sa': 9,  // Saudi Arabia
        'ae': 9,  // UAE
        'kw': 8,  // Kuwait
        'qa': 8,  // Qatar
        'bh': 8,  // Bahrain
        'om': 8,  // Oman
        'ye': 9,  // Yemen
        'iq': 10, // Iraq
        'ir': 10, // Iran
        'il': 9,  // Israel
        'jo': 9,  // Jordan
        'lb': 8,  // Lebanon
        'sy': 9,  // Syria
        'ps': 9,  // Palestine
        'tr': 10, // Turkey
        'eg': 10, // Egypt
        'ly': 9,  // Libya
        'tn': 8,  // Tunisia
        'dz': 9,  // Algeria
        'ma': 9,  // Morocco
        'sd': 9,  // Sudan
        'et': 9,  // Ethiopia
        
        // Asia
        'cn': 11, // China
        'jp': 10, // Japan
        'kr': 10, // South Korea
        'tw': 9,  // Taiwan
        'hk': 8,  // Hong Kong
        'mo': 8,  // Macau
        'sg': 8,  // Singapore
        'my': 9,  // Malaysia
        'th': 9,  // Thailand
        'vn': 10, // Vietnam
        'ph': 10, // Philippines
        'id': 9,  // Indonesia
        'mm': 9,  // Myanmar
        'kh': 9,  // Cambodia
        'la': 8,  // Laos
        'bn': 7,  // Brunei
        'bd': 10, // Bangladesh
        'pk': 10, // Pakistan
        'lk': 9,  // Sri Lanka
        'np': 10, // Nepal
        'af': 7,  // Afghanistan
        'uz': 9,  // Uzbekistan
        'tm': 8,  // Turkmenistan
        'tj': 9,  // Tajikistan
        'kg': 9,  // Kyrgyzstan
        'mn': 8,  // Mongolia
        
        // Oceania
        'pg': 8,  // Papua New Guinea
        'fj': 7,  // Fiji
        'nc': 6,  // New Caledonia
        'pf': 6,  // French Polynesia
        'ws': 7,  // Samoa
        'to': 7,  // Tonga
        'vu': 7,  // Vanuatu
        'sb': 7,  // Solomon Islands
        'ki': 5,  // Kiribati
        'tv': 6,  // Tuvalu
        'nr': 7,  // Nauru
        'pw': 7,  // Palau
        'fm': 7,  // Micronesia
        'mh': 7,  // Marshall Islands
        
        // Africa
        'ng': 10, // Nigeria
        'ke': 9,  // Kenya
        'tz': 9,  // Tanzania
        'ug': 9,  // Uganda
        'rw': 9,  // Rwanda
        'gh': 9,  // Ghana
        'ci': 8,  // Côte d'Ivoire
        'sn': 9,  // Senegal
        'ml': 8,  // Mali
        'bf': 8,  // Burkina Faso
        'ne': 8,  // Niger
        'td': 8,  // Chad
        'cm': 9,  // Cameroon
        'ga': 8,  // Gabon
        'cg': 9,  // Republic of the Congo
        'cd': 9,  // DR Congo
        'ao': 9,  // Angola
        'zm': 9,  // Zambia
        'zw': 9,  // Zimbabwe
        'bw': 8,  // Botswana
        'na': 9,  // Namibia
        'mw': 9,  // Malawi
        'mz': 9,  // Mozambique
        'mg': 9,  // Madagascar
        'mu': 8,  // Mauritius
        'sc': 7,  // Seychelles
        'km': 7,  // Comoros
        'dj': 8,  // Djibouti
        'er': 7,  // Eritrea
        'so': 8,  // Somalia
        'ss': 9,  // South Sudan
        
        // Caribbean
        'ht': 8,  // Haiti
        'pr': 10, // Puerto Rico
        'vi': 10, // US Virgin Islands
        'vg': 10, // British Virgin Islands
        'ky': 7,  // Cayman Islands
        'bm': 10, // Bermuda
        'tc': 7,  // Turks and Caicos
        'ai': 7,  // Anguilla
        'ms': 7,  // Montserrat
        'gd': 7,  // Grenada
        'lc': 7,  // Saint Lucia
        'vc': 7,  // Saint Vincent
        'ag': 10,  // Antigua and Barbuda
        'dm': 7,  // Dominica
        'kn': 7,  // Saint Kitts and Nevis
        
        // Other territories
        'fo': 6,  // Faroe Islands
        'gl': 6,  // Greenland
        'sj': 8,  // Svalbard
        'ax': 6,  // Åland Islands
        'gg': 10, // Guernsey
        'je': 10, // Jersey
        'im': 10, // Isle of Man
        'ad': 6,  // Andorra
      };
      
      return countryMaxLengths[regionCode] || 15; // Default to 15 for unknown countries
    }

    onPhoneNumberInput(event: any) {
        const countryCode = this.companyDetailForm.get('contactPersonPhoneCountryCode')?.value;
        const formattedNumber = this.formatPhoneNumber(event.target.value, countryCode);
        this.companyDetailForm.get('contactPersonPhone')?.setValue(formattedNumber);
        this.companyDetailForm.get('contactPersonPhone')?.updateValueAndValidity();
      }
    
    onContactPersonPhoneCountryChange(country: any) {
        if (country && country.iso2) {
            this.companyDetailForm.get('contactPersonPhoneCountryCode')?.setValue(country.iso2);
            // Reset phone number field when country changes
            this.companyDetailForm.get('contactPersonPhone')?.setValue('', { emitEvent: false });
        }
    }
}